// HireAra Color Palette
// Based on the brand colors from index.css

export const araColors = {
	// Primary brand colors - Based on design system
	blue: "#24B5D1", // Primary color - Ara Blue
	purple: "#5114FE", // Secondary color - Purple
	green: "#00C896", // Accent color - Green
	darkBlue: "#1E3A8A", // Dark blue from design system
	lavender: "#ECE7FF", // Lavender tint

	// Neutrals - Dark theme
	neutralDark: {
		100: "#ffffff",
		80: "#cccccc",
		60: "#999999",
		40: "#666666",
		20: "#333333",
		5: "#0d0d0d",
	},

	// Neutrals - Light theme
	neutralLight: {
		100: "#000000",
		80: "#333333",
		60: "#666666",
		40: "#999999",
		20: "#cccccc",
		5: "#f2f2f2",
	},

	// Status colors - Updated to match design system
	success: "#00C896", // Green (accent color)
	error: "#e30000", // Keep existing red
	warning: "#daac1c", // Keep existing warning

	// Additional colors - Updated for consistency
	lightGreen: "#b6ebd7", // Keep existing light green
	gold: "#daac1c", // Keep existing gold
	darkGold: "#755b0d", // Keep existing dark gold
	red: "#e30000", // Keep existing red

	// Solid colors
	white: "#ffffff",
	black: "#000000",
} as const

// Semantic color mappings
export const semanticColors = {
	background: {
		primary: araColors.neutralDark[100],
		secondary: araColors.neutralLight[5],
	},
	text: {
		primary: araColors.neutralDark[5],
		secondary: araColors.neutralDark[20],
		brand: araColors.blue,
		white: araColors.white,
	},
	border: {
		primary: araColors.neutralLight[20],
		brand: araColors.blue,
	},
	input: {
		background: araColors.neutralDark[100],
		backgroundHover: araColors.neutralLight[5],
	},
	// Brand color hierarchy for consistent usage
	brand: {
		primary: araColors.blue, // #24B5D1 - main brand color
		secondary: araColors.purple, // #5114FE - secondary actions
		accent: araColors.green, // #00C896 - accent elements
	},
} as const

// Mantine color arrays (10 shades from light to dark)
export const mantineColorShades = {
	araBlue: [
		"#e6f8fc",
		"#b3edf4",
		"#80e1ed",
		"#4dd5e5",
		"#26b4d1",
		"#24B5D1", // Main shade (index 5) - Updated to match design system
		"#1e93a8",
		"#17707e",
		"#0f4d54",
		"#08292a",
	],
	araPurple: [
		"#f5f0ff",
		"#e6d9ff",
		"#cdb3ff",
		"#b38cff",
		"#9a66ff",
		"#5114FE", // Main shade (index 5) - Matches design system
		"#410fe5",
		"#310acb",
		"#2106b2",
		"#100398",
	],
	araGreen: [
		"#e6fdf7",
		"#b3f9e8",
		"#80f4d9",
		"#4defc9",
		"#1aebba",
		"#00C896", // Main shade (index 5) - New accent color
		"#00a077",
		"#007858",
		"#005039",
		"#00281a",
	],
	araRed: [
		"#fee6e6",
		"#fcb3b3",
		"#f98080",
		"#f64d4d",
		"#f41a1a",
		"#e30000", // Main shade (index 5) - Existing red color
		"#bf0000",
		"#9c0000",
		"#790000",
		"#560000",
	],
	araDark: [
		"#eef2ff",
		"#c2d4ff",
		"#96b6ff",
		"#6a98ff",
		"#3e7aff",
		"#1E3A8A", // Main shade (index 5) - Updated to match design system
		"#182e6b",
		"#12224d",
		"#0c162e",
		"#060a0f",
	],
} as const

export type AraColors = typeof araColors
export type SemanticColors = typeof semanticColors
export type MantineColorShades = typeof mantineColorShades
